import React, { useEffect, useRef, useState } from 'react';

import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { ListBox } from 'primereact/listbox';
import { Menu } from 'primereact/menu';
import { OverlayPanel } from 'primereact/overlaypanel';

import { CustomDropdown } from 'ui/custom-controls';
import Datepicker from 'ui/custom-controls/Datepicker';

import { selectedDate, TreeNode } from '../../types/interfaces';

interface TaskMobileFiltersDialogProps {
  visible: boolean;
  onHide: () => void;
  searchFilter: string;
  setSearchFilter: (value: string) => void;
  handleFilterChange: (filter: string, value: string) => void;
  datepickerDate: selectedDate;
  handleDateFilterChange: (selection: selectedDate) => void;
  hasHorizontalScroll: boolean;
  treeDropDownValue: TreeNode[];
  selectedDepartmentsFilter: TreeNode[];
  handleTreeSelectionChange: (department: TreeNode | TreeNode[]) => void;
  openMap: () => void;
  setOpenDepartmentAssignmentDialog: (value: boolean) => void;
  openDepartmentAssignmentDialog: boolean;
  setEmployeeDistributionDialogVisible: (value: boolean) => void;
  exportTemplate: () => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  menu: React.RefObject<Menu>;
}

interface SearchFilterLabel {
  name: string;
  code: string;
}

const filtersLabel: SearchFilterLabel[] = [
  { name: 'Дата регистрации наряда', code: 'creationDateBetween' },
  { name: 'Дата начала работы (план)', code: 'plannedStartDateBetween' },
  { name: 'Дата начала работы (факт)', code: 'actualStartDateBetween' },
  { name: 'Дата завершения работы (план)', code: 'plannedEndDateBetween' },
  { name: 'Дата завершения работы (факт)', code: 'actualEndDateBetween' }
];

const getLast30DaysDates = (): selectedDate => {
  const now = new Date();
  const thirtyDaysAgo = new Date(now);
  thirtyDaysAgo.setDate(now.getDate() - 30);

  return {
    dateFrom: thirtyDaysAgo.getTime().toString(),
    dateUntil: now.getTime().toString()
  };
};

const TaskMobileFiltersDialog: React.FC<TaskMobileFiltersDialogProps> = ({
  visible,
  onHide,
  searchFilter,
  setSearchFilter,
  handleFilterChange,
  datepickerDate,
  handleDateFilterChange,
  hasHorizontalScroll,
  treeDropDownValue,
  selectedDepartmentsFilter,
  handleTreeSelectionChange,
  openMap,
  setOpenDepartmentAssignmentDialog,
  openDepartmentAssignmentDialog,
  setEmployeeDistributionDialogVisible,
  exportTemplate,
  fileInputRef,
  menu
}) => {
  const op = useRef<OverlayPanel>(null);

  const [localSearchFilter, setLocalSearchFilter] =
    useState<string>(searchFilter);
  const [localDatepickerDate, setLocalDatepickerDate] =
    useState<selectedDate>(datepickerDate);
  const [localDepartmentsFilter, setLocalDepartmentsFilter] = useState<
    TreeNode[]
  >(selectedDepartmentsFilter);
  const [selectedFilterLabel, setSelectedFilterLabel] =
    useState<SearchFilterLabel | null>(null);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  useEffect(() => {
    if (visible && !isInitialized) {
      const savedSearchFilter = localStorage.getItem('searchFilter');
      const savedDateFilter = localStorage.getItem('selectedDateFilter');

      let initialSearchFilter = searchFilter || savedSearchFilter || '';
      let initialDate = datepickerDate;

      const normalizeDate = (date: selectedDate): selectedDate => {
        const convertToTimestamp = (value: string): string => {
          if (!value) return '';
          if (value.includes('T') || value.includes('Z')) {
            return new Date(value).getTime().toString();
          }
          return value;
        };

        return {
          dateFrom: convertToTimestamp(date.dateFrom),
          dateUntil: convertToTimestamp(date.dateUntil)
        };
      };

      if (savedDateFilter) {
        try {
          const parsed = JSON.parse(savedDateFilter);
          if (
            parsed.dateFrom &&
            parsed.dateFrom !== '' &&
            parsed.dateUntil &&
            parsed.dateUntil !== ''
          ) {
            initialDate = normalizeDate(parsed);
          } else {
            initialDate = getLast30DaysDates();
          }
        } catch (e) {
          console.error('Error parsing saved date filter:', e);
        }
      }

      if (savedSearchFilter && !searchFilter) {
        initialSearchFilter = savedSearchFilter;
      }

      setLocalSearchFilter(initialSearchFilter);
      setLocalDatepickerDate(initialDate);
      setLocalDepartmentsFilter(
        selectedDepartmentsFilter.length === 0
          ? [{ key: 'selectAllId', label: 'Выбрать все участки' }]
          : selectedDepartmentsFilter
      );

      const foundFilter = filtersLabel.find(
        (f) => f.code === initialSearchFilter
      );
      if (foundFilter) {
        setSelectedFilterLabel(foundFilter);
      }

      setIsInitialized(true);
    }

    if (!visible) {
      setIsInitialized(false);
    }
  }, [
    visible,
    searchFilter,
    datepickerDate,
    selectedDepartmentsFilter,
    isInitialized,
    filtersLabel
  ]);

  const handleFilterLabelChange = (e: { value: SearchFilterLabel }) => {
    if (!e.value) {
      op.current?.hide();
      return;
    }
    const selected = e.value;
    setSelectedFilterLabel(selected);
    setLocalSearchFilter(selected.code);
    op.current?.hide();
  };

  const handleApplyFilters = () => {
    setSearchFilter(localSearchFilter);
    handleDateFilterChange(localDatepickerDate);
    handleTreeSelectionChange(localDepartmentsFilter);

    localStorage.setItem('searchFilter', localSearchFilter);
    localStorage.setItem(
      'selectedDateFilter',
      JSON.stringify(localDatepickerDate)
    );

    onHide();
  };

  const setDatePickerTooltip = () => {
    switch (localSearchFilter) {
      case 'creationDateBetween':
        return 'Фильтрация по дате регистрации наряда';
      case 'plannedStartDateBetween':
        return 'Фильтрация по планируемой дате начала работ';
      case 'actualStartDateBetween':
        return 'Фильтрация по фактической дате начала работ';
      case 'plannedEndDateBetween':
        return 'Фильтрация по планируемой дате завершения работ';
      case 'actualEndDateBetween':
        return 'Фильтрация по фактической дате завершения работ';
      default:
        return '';
    }
  };

  const handleLocalTreeSelectionChange = (
    department: TreeNode | TreeNode[]
  ) => {
    const selection = Array.isArray(department) ? department : [department];
    setLocalDepartmentsFilter(selection);
  };

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header="Фильтры и управление"
      style={{ width: '95vw', maxWidth: '400px' }}
      breakpoints={{ '960px': '95vw' }}
      className="mobile-filters-dialog"
    >
      <div className="flex flex-column gap-3 p-3">
        <div className="flex flex-column gap-2">
          <label className="font-medium">Фильтр по дате</label>
          <div className="w-full" style={{ position: 'relative' }}>
            <InputText
              value={selectedFilterLabel?.name || ''}
              placeholder="Выберите тип фильтра"
              className="w-full"
              style={{ cursor: 'pointer' }}
              onClick={(e) => op.current?.toggle(e)}
              readOnly
            />
            <i
              className="pi pi-chevron-down"
              style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#6c757d'
              }}
            />

            <OverlayPanel
              ref={op}
              showCloseIcon
              dismissable
              style={{ width: '100%', maxWidth: '350px' }}
              pt={{
                content: {
                  style: {
                    padding: 0
                  }
                }
              }}
            >
              <div className="flex flex-column gap-3">
                <ListBox
                  value={selectedFilterLabel}
                  onChange={handleFilterLabelChange}
                  options={filtersLabel}
                  optionLabel="name"
                  className="w-full"
                />
              </div>
            </OverlayPanel>
          </div>
        </div>

        <div className="flex flex-column gap-2">
          <label className="font-medium">Диапазон дат</label>
          <div className="w-full">
            <Datepicker
              datepickerDate={localDatepickerDate}
              onChange={setLocalDatepickerDate}
              showTooltips={false}
              customTooltips={{
                from: setDatePickerTooltip(),
                until: setDatePickerTooltip()
              }}
              hasHorizontalScroll={hasHorizontalScroll}
            />
          </div>
        </div>

        <div className="flex flex-column gap-2">
          <label className="font-medium">Участки</label>
          <div className="w-full">
            <CustomDropdown
              assetOptions={[]}
              treeValue={treeDropDownValue}
              useTree={true}
              selectedAsset={localDepartmentsFilter}
              onTreeSelectionChange={handleLocalTreeSelectionChange}
              placeholder={
                localDepartmentsFilter.length > 0
                  ? `Выбрано участков: ${localDepartmentsFilter.length}`
                  : 'Выберите участки'
              }
              showClearOnHover={false}
              onToggleShowAll={() => {}}
              showAll={false}
              minWidth={200}
              multiplySelectMode
              overlayClassName="mobile-department-overlay"
            />
          </div>
        </div>

        <div className="flex gap-2 mt-2">
          <Button
            label="Применить фильтры"
            className="flex-1"
            onClick={handleApplyFilters}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default TaskMobileFiltersDialog;
